export enum FlowStep {
    MATERIALS,
    GEOMETRY,
    LOADCASES,
    MANUFACTURING,
    GOALS,
    RESULTS
}

export const FlowStepName = [
    "Materials",
    "Geometry",
    "Loadcases",
    "Manufacturing",
    "Goals",
    "Results"
];

export enum FlowStepInner {
    NONE,
    LOAD_TYPE,
    BOUNDARY_CONDITIONS,
    LOADS,
    ACCELERATION_SETTINGS,
    EDIT_LOAD,
    ADD_GROUP,
    EDIT_GROUP,
    SELECT_FACES,
    BASE_PARTS
}

export const FlowStepInnerName = [
    "None",
    "Load Type",
    "Boundary Conditions",
    "Loads",
    "Acceleration Settings",
    "Edit Load",
    "Add Group",
    "Edit Group",
    "Select Faces",
    "Base"
]

export enum SideMenuView {
    MODELS,
    FLOW,
    WIZARD,
    UCS,
    SETTINGS,
    SOFTJAWS,
    RIBS,
    BOSSES,
    CAD_EDITOR,
    PROJECT_SETTINGS,
    RESULTS
}

export enum LoadCasesFlowStep {
    LOAD_TYPE,
    BOUNDARY_CONDITIONS,
    LOADS,
    ACCELERATION_SETTINGS,
    EDIT_LOAD,
    ADD_GROUP,
    EDIT_GROUP,
    SELECT_FACES,
    BASE_PARTS,
    /** Manual preserve-face selection. Engine selector enters face-pick
     *  mode but suppresses hover/unhover repaints — only click → yellow
     *  (selected) is visible. Used by the AI assistant's manual preserve
     *  workflow; not a real load-case step. */
    PRESERVES
}

export enum PartType {
    DESIGN,
    NONDESIGN,
    NONE
}

export enum PartSubType {
    BOUNDARY_CONDITION,
    LOAD,
    BASE,
    NONE
}

export enum ActionType {
    LIST,
    ADD,
    EDIT
}

export enum LoadCaseType {
    STATIC,
    THERMO_MECHANICAL,
    VIBRATION,
    HARMONIC_EXCITATION,
    RANDOM_VIBRATION,
    HARMONIC_EXCITATION_BASE,
    RANDOM_VIBRATION_BASE
}

export enum LoadType {
    FORCE,
    PRESSURE,
    ACCELERATION
}

export enum ManufacturingMethod {
    AdditiveManufacturing = "Additive Manufacturing",
    Milling = "Milling",
    Milling2 = "Milling2",
    InjectionMoldingWithDrawDirection = "Injection Molding with draw direction",
    DieCasting = "Die Casting",
    Extrusion = "Extrusion",
    InjectionMoldingWithExtrusion = "Injection Molding with extrusion",
    InvestmentCasting = "InvestmentCasting",
}

export enum ExtrusionPlaneGenerationMode {
    Even = "Even",
    GenerateFromDesDomain = "GenerateFromDesDomain",
    GenerateFromInitGuess = "GenerateFromInitGuess",
}

export enum DesignObjective {
    MinimizeCompliance = "compliance-response",
    MinimizeWeightedCompliance = "weighted-compliance-response",
    MinimizeMass = "mass-response",
    MaximizationOfFundamentalFrequencies = "vibration-response",
    MinimumVolumeUnderFrequencyConstraint = "frequency",
    MinimizeMassRMS = "mass-rms-response",
    MinimizeStressRMS = "stress-rms-response"
}

export enum MaterialCategory {
    Metal = "Metal",
    Polymer = "Polymer",
    Composite = "Composite",
    Ceramic = "Ceramic",
    Alloy = "Alloy"
}

export enum MaterialType {
    Aluminum = "Aluminum",
    Steel = "Steel",
    Titanium = "Titanium",
    Polymer = "Polymer",
    Composite = "Composite",
    Nickel = "Nickel",
    Cobalt = "Cobalt",
    Copper = "Copper",
    Ceramic = "Ceramic",
}

export enum RunType {
    Standard = 0,
    FiniteElementAnalysis = 1
}

/**
 * Label for status 7 ("in progress") that reflects whether the run is a
 * normal optimization or an FEA-only analysis. "Running" for FEA,
 * "Optimizing" otherwise. Used across cabinet list, sharing, compare
 * tile, and desktop tray.
 */
export function inProgressLabel(runType?: number | null): string {
    return runType === RunType.FiniteElementAnalysis ? 'Running' : 'Optimizing';
}

/**
 * Mirrors ProjectStatus in backend/src/resources/project/entities/project.entity.ts.
 * The ordinals are persisted, so the order here must not change.
 *
 * Note the backend's PATCH :id/status maps the string 'processing' to
 * OptimizePreprocessing (6), not Busy (3) — a soft-jaws run reports itself as 6
 * once the Lambda picks it up, and sits at Busy(3) only between the run request
 * and that first callback.
 */
export enum ProjectStatus {
    Import = 0,
    Ready = 1,
    Error = 2,
    Busy = 3,
    Done = 4,
    BusyDuplicate = 5,
    OptimizePreprocessing = 6,
    OptimizeOptimization = 7,
    OptimizePostprocessing = 8,
    OptimizeFinalizing = 9,
    Queued = 10
}

/** True while a soft-jaws run is queued or executing. */
export function isSoftJawsRunning(status: number | null | undefined): boolean {
    return status === ProjectStatus.Busy || status === ProjectStatus.OptimizePreprocessing;
}

export enum ProjectType {
    TopologyOptimization = "topology_optimization",
    SoftJaws = "soft_jaws",
    Mold = "mold"
}

// Filter-only value for the cabinet "Project Type" dropdowns: selects
// topology projects whose config.runType is FiniteElementAnalysis.
// Never stored on a Project document. Mirrors backend temporary/enums.ts.
export const FEA_PROJECT_TYPE_FILTER = "fea";

export type MassType = 'nonStructural' | 'remotePoint';

// Explicit RemoteMass reference point (pivot). null = "follow the remote point"
// (zero offset — the original formulation). Mirrors backend temporary/enums.ts.
export function normalizeReferencePoint(input?: unknown): number[] | null {
    if (!Array.isArray(input) || input.length !== 3) return null;
    const values = input.map((v) => Number(v));
    return values.every((v) => Number.isFinite(v)) ? values : null;
}

// The pivot the solver actually uses: the explicit reference point when set,
// otherwise the remote point itself (zero offset).
export function effectiveReferencePoint(item: { remotePoint: number[]; referencePoint?: number[] | null }): number[] {
    return normalizeReferencePoint(item.referencePoint) ?? item.remotePoint;
}

// Remote mass centroidal inertia tensor [I11, I22, I33, I23, I13, I12] in kg·mm².
// All-zero = point mass (the pre-Icm optimizer formulation). Mirrors backend temporary/enums.ts.
export function normalizeInertiaTensor(input?: unknown): number[] {
    const zeros = [0, 0, 0, 0, 0, 0];
    if (!Array.isArray(input)) return zeros;
    return zeros.map((_, i) => {
        const value = Number(input[i]);
        return Number.isFinite(value) ? value : 0;
    });
}

// Mirrors the 2-decimal remotePoint/centerOfMass comparison both XML ladders
// used before massType existed — legacy documents must resolve identically.
export function deriveMassType(remotePoint?: number[], centerOfMass?: number[]): MassType {
    if (!remotePoint || !centerOfMass) return 'nonStructural';
    const to2 = (v: number): number => parseFloat(parseFloat(String(v)).toFixed(2));
    const same =
        to2(remotePoint[0]) === to2(centerOfMass[0]) &&
        to2(remotePoint[1]) === to2(centerOfMass[1]) &&
        to2(remotePoint[2]) === to2(centerOfMass[2]);
    return same ? 'nonStructural' : 'remotePoint';
}
